import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, withKeys as _withKeys, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "card" }
const _hoisted_2 = { class: "flex flex-row flex-nowrap card-header border-0" }
const _hoisted_3 = { class: "flex card-toolbar w-full" }
const _hoisted_4 = { class: "w-full" }
const _hoisted_5 = { class: "p-input-icon-left w-10/12" }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("i", { class: "pi pi-search" }, null, -1)
const _hoisted_7 = { class: "card-toolbar w-full" }
const _hoisted_8 = { class: "flex flex-row items-center justify-between" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { key: 1 }
const _hoisted_11 = { class: "card-body pt-0" }

import { SocialEventBrands } from "@/models/product/brands/SocialEventBrands";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { ref, onMounted, watch, computed } from "vue";
import InputText from "primevue/inputtext";
import swal from "@/_helpers/swal";
import router from "@/router";
import { getPaginatedSupplierForDropDown } from "@/_services/supplier/supplier";
import { fetchBrandsByUrssafCategory } from "@/_services/category/category";
import Dropdown from "primevue/dropdown";

const tableLocalStorageKey = "tableSelectedBrandsToAddInCollection";
const defaultRowsPerPage = 10;

export default /*#__PURE__*/_defineComponent({
  __name: 'PageMappingCategoryUrssafBrandList',
  setup(__props) {

const tableData = ref<SocialEventBrands[]>([]);
const rows = ref<number>(10);
const isLoading = ref<boolean>(true);
const totalRecords = ref<number>(0);
const currentPage = ref<number>(0);
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100, 250];
const page = ref<number>(1);
const criteria = ref<string>("");
const selectedSupplier = ref<{name: string} | null>(null);
const uniqueSuppliers = ref<{name: string}[]>([]);
const originalBrands = ref<SocialEventBrands[]>([]);

const filteredTableData = computed(() => {
    if (!criteria.value && !selectedSupplier.value) {
        return tableData.value;
    }
    
    return tableData.value.filter(item => {
        const matchesBrand = !criteria.value || 
            item.brand.toLowerCase().includes(criteria.value.toLowerCase());
        
        const matchesSupplier = !selectedSupplier.value || 
            item.supplier === selectedSupplier.value.name;
        
        return matchesBrand && matchesSupplier;
    });
});

const totalFilteredRecords = computed(() => {
    return filteredTableData.value.length;
});

const paginatedData = computed(() => {
    const start = currentPage.value * rows.value;
    const end = start + rows.value;
    return filteredTableData.value.slice(start, end);
});

function onSearchInputDt() {
    showSpinner();
    setTimeout(() => {
        isLoading.value = false;
    }, 300);
}

watch(criteria, () => {
    if (criteria.value.length >= 3 || criteria.value.length === 0) {
        onSearchInputDt();
    }
});

onMounted(async () => {
    await populateDataTableWithLocalStorageFilters();
});

function extractUniqueSuppliers(brands: SocialEventBrands[]): {name: string}[] {
    const supplierSet = new Set<string>();
    
    brands.forEach(brand => {
        if (brand.supplier) {
            supplierSet.add(brand.supplier);
        }
    });
    
    return Array.from(supplierSet).map(supplier => ({ name: supplier }));
}

function onSupplierFilterChange() {
    onSearchInputDt();
}

const populateDatatable = async () => {
    showSpinner();

    const socialEventUuid = router.currentRoute.value.params.uuid;

    if (!socialEventUuid) {
        swal.error("Aucun identifiant d'événement URSSAF trouvé dans l'URL.");
        return;
    }

    try {
        isLoading.value = true;

        const {
            brands,
            errorMessage
        } = await fetchBrandsByUrssafCategory(socialEventUuid);

        if (Array.isArray(brands.value)) {
            const formattedBrands = brands.value.map((brandInfo: any) => {
                if (typeof brandInfo === 'object' && brandInfo.brand && brandInfo.supplier) {
                    return {
                        brand: brandInfo.brand,
                        supplier: brandInfo.supplier,
                        isSelected: false
                    };
                } else if (typeof brandInfo === 'string') {
                    const parts = brandInfo.split('|');
                    return {
                        brand: parts[0] || '',
                        supplier: parts[1] || '',
                        isSelected: false
                    };
                }
                return {
                    brand: '',
                    supplier: '',
                    isSelected: false
                };
            });
            
            originalBrands.value = formattedBrands;
            tableData.value = formattedBrands;
            totalRecords.value = formattedBrands.length;
            
            uniqueSuppliers.value = extractUniqueSuppliers(formattedBrands);
        } else {
            tableData.value = [];
            totalRecords.value = 0;
        }

        isLoading.value = false;
    } catch (err: any) {
        swal.error("Une erreur inattendue s'est produite.");
    }
};

async function populateDataTableWithLocalStorageFilters(itemsPerPage?: number, page?: number) {
    let localStorageRowPerPage = getLocalStorageRowsPerPage();
    let localStorageFisrtPageState = getLocalStorageFirstPageState();
    let currentPage = page;
    if (!currentPage) {
        currentPage = localStorageFisrtPageState / localStorageRowPerPage + 1;
    }
    let currentItemsPerPage = itemsPerPage;
    if (!currentItemsPerPage) {
        currentItemsPerPage = localStorageRowPerPage;
    }

    await populateDatatable();
}

const onPaginate = async (e) => {
    showSpinner();
    rows.value = e.rows;
    currentPage.value = e.page;
    page.value = e.page + 1;
    setLocalStorageRowsPerPage(rows.value);
    hideSpinner();
};

const hideSpinner = () => {
    isLoading.value = false;
}

const showSpinner = () => {
    isLoading.value = true;
}

function getLocalStorageData() {
    let storageData = localStorage.getItem(tableLocalStorageKey);
    if (null !== storageData && "undefined" !== storageData) {
        return JSON.parse(storageData);
    }

    return null;
}

function setLocalStorageRowsPerPage(rowPerPage: number) {
    let storageData = localStorage.getItem(tableLocalStorageKey);
    if (storageData) {
        let localStorageObject = JSON.parse(storageData);
        localStorageObject.rows = rowPerPage;
        localStorage.setItem(tableLocalStorageKey, JSON.stringify(localStorageObject));
    }
}

function getLocalStorageRowsPerPage() {
    let localStorageData = getLocalStorageData();
    if (null !== localStorageData && localStorageData.rows) {
        return localStorageData.rows;
    }

    return defaultRowsPerPage;
}

function getLocalStorageFirstPageState() {
    let localStorageData = getLocalStorageData();
    if (null !== localStorageData && localStorageData.first) {
        return localStorageData.first;
    }

    return 0;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(DataTable), {
      lazy: "",
      dataKey: "uuid",
      stateStorage: "local",
      ref: "dt",
      "state-key": tableLocalStorageKey,
      value: paginatedData.value,
      paginator: true,
      rows: rows.value,
      rowHover: true,
      sortMode: "multiple",
      removableSort: "",
      loading: isLoading.value,
      totalRecords: totalFilteredRecords.value,
      currentPage: currentPage.value,
      paginatorTemplate: "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",
      rowsPerPageOptions: rowsPerPageOptions,
      currentPageReportTemplate: "Affichage de {first} à {last} sur {totalRecords} entrées",
      onPage: _cache[3] || (_cache[3] = ($event: any) => (onPaginate($event)))
    }, {
      header: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("span", _hoisted_5, [
                _hoisted_6,
                _createVNode(_unref(InputText), {
                  class: "w-9/12",
                  type: "text",
                  modelValue: criteria.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((criteria).value = $event)),
                  placeholder: "Rechercher une marque",
                  onKeyup: _cache[1] || (_cache[1] = _withKeys(($event: any) => (onSearchInputDt()), ["enter"]))
                }, null, 8, ["modelValue"])
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _createVNode(_unref(Dropdown), {
                modelValue: selectedSupplier.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedSupplier).value = $event)),
                options: uniqueSuppliers.value,
                optionLabel: "name",
                placeholder: "Sélectionner un fournisseur",
                filter: "",
                showClear: true,
                class: "w-64",
                onChange: onSupplierFilterChange
              }, {
                value: _withCtx((slotProps) => [
                  (slotProps.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(slotProps.value.name), 1))
                    : (_openBlock(), _createElementBlock("span", _hoisted_10, "Sélectionner un fournisseur"))
                ]),
                option: _withCtx((slotProps) => [
                  _createElementVNode("div", null, _toDisplayString(slotProps.option.name), 1)
                ]),
                _: 1
              }, 8, ["modelValue", "options"])
            ])
          ])
        ])
      ]),
      empty: _withCtx(() => [
        _createTextVNode("Aucun résultat trouvé.")
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_11, [
          _createVNode(_unref(Column), {
            field: "brand",
            header: "Marque"
          }, {
            body: _withCtx((slotProps) => [
              _createElementVNode("span", null, _toDisplayString(slotProps.data.brand), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(Column), {
            field: "supplier",
            header: "Fournisseur"
          }, {
            body: _withCtx((slotProps) => [
              _createElementVNode("span", null, _toDisplayString(slotProps.data.supplier), 1)
            ]),
            _: 1
          })
        ])
      ]),
      _: 1
    }, 8, ["value", "rows", "loading", "totalRecords", "currentPage"])
  ]))
}
}

})